// Translates GET /account into the two forms on this page, and the edits back
// into a PATCH body.
//
// The response pairs a personal profile with a workspace profile. Both may be
// nested under their own key or sit flat at the root, and field names vary
// (fullName/name, avatarUrl/avatar), so each is read through a short list of
// aliases rather than one guessed spelling.

const firstOf = (source, keys) => {
  if (!source) return undefined;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) return value;
  }

  return undefined;
};

const sectionOf = (account, keys, { fallbackToRoot = false } = {}) => {
  const named = firstOf(account, keys);
  if (named && typeof named === "object") return named;

  return fallbackToRoot ? account || {} : {};
};

/**
 * @param account the response payload
 * @param defaults the form's starting values, kept for anything unsent
 */
export const accountToProfile = (account, defaults) => {
  // A flat payload is the personal profile, so this one may read the root.
  const profile = sectionOf(
    account,
    ["profile", "personal", "user", "account"],
    { fallbackToRoot: true },
  );

  return {
    ...defaults,
    avatar: firstOf(profile, ["avatarUrl", "avatar", "photoUrl"]) ?? defaults.avatar,
    fullName:
      firstOf(profile, ["fullName", "name", "displayName"]) ?? defaults.fullName,
    email: firstOf(profile, ["email"]) ?? defaults.email,
    jobTitle: firstOf(profile, ["jobTitle", "title", "role"]) ?? defaults.jobTitle,
    phone: firstOf(profile, ["phone", "phoneNumber", "mobile"]) ?? defaults.phone,
    timezone: firstOf(profile, ["timezone", "timeZone"]) ?? defaults.timezone,
    language: firstOf(profile, ["language", "locale"]) ?? defaults.language,
  };
};

export const accountToWorkspace = (account, defaults) => {
  // Never the root: a flat payload's `name` is the person's, and claiming it
  // here would label the workspace with the user's own name.
  const workspace = sectionOf(account, [
    "workspace",
    "organization",
    "organisation",
    "org",
    "company",
  ]);

  return {
    ...defaults,
    logo: firstOf(workspace, ["logoUrl", "logo"]) ?? defaults.logo,
    name: firstOf(workspace, ["name", "workspaceName", "companyName"]) ?? defaults.name,
    website: firstOf(workspace, ["website", "websiteUrl", "url"]) ?? defaults.website,
    industry: firstOf(workspace, ["industry", "sector"]) ?? defaults.industry,
    size: firstOf(workspace, ["size", "companySize", "teamSize"]) ?? defaults.size,
    slug: firstOf(workspace, ["slug", "handle"]) ?? defaults.slug,
  };
};

// The form's keys, in the spelling the API uses.
const PROFILE_FIELDS = {
  fullName: "fullName",
  jobTitle: "jobTitle",
  phone: "phone",
  timezone: "timezone",
  language: "language",
  avatar: "avatarUrl",
};

const WORKSPACE_FIELDS = {
  name: "name",
  website: "website",
  industry: "industry",
  size: "size",
  slug: "slug",
  logo: "logoUrl",
};

const changedFields = (current, original, mapping) =>
  Object.entries(mapping).reduce((payload, [formKey, apiKey]) => {
    if (current?.[formKey] !== original?.[formKey]) {
      payload[apiKey] = current?.[formKey] ?? null;
    }
    return payload;
  }, {});

/**
 * Only what actually changed, as the endpoint asks for. Email is left out:
 * it's the account identity and isn't editable here.
 *
 * @returns {object} the PATCH body, empty when nothing was touched
 */
export const accountPayload = (profile, workspace, original) => {
  const body = changedFields(profile, original.profile, PROFILE_FIELDS);
  const workspaceBody = changedFields(
    workspace,
    original.workspace,
    WORKSPACE_FIELDS,
  );

  if (Object.keys(workspaceBody).length) body.workspace = workspaceBody;

  return body;
};
